// 🔒 LOCKED - DO NOT MODIFY WITHOUT EXPLICIT PERMISSION
import { apiClient } from '../../utils/apiHelper';
// This component is working correctly and has been tested
// Last modified: 2025-12-08
// Critical: Fee installment receipts (FEERCT*) now showing correctly
// Uses xlsx library for Excel export - do not remove dependency
// Any changes may break the Collection Day Book Report functionality

import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, TrendingUp, Download, FileText, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// SECURITY FIX: Read token at request time (not module load) to avoid stale
// token bleeding across tenant logins which broke tenant isolation.
const getToken = () => localStorage.getItem('token');

const CollectionDayBook = () => {
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [collectionData, setCollectionData] = useState({ summary: {}, collections: [] });
  const [loading, setLoading] = useState(false);

  

  useEffect(() => {
    fetchCollectionDayBook();
  }, []);

  const fetchCollectionDayBook = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const response = await axios.get(
        `${API_URL}/api/fees/collection-daybook?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      setCollectionData(response.data);
    } catch (error) {
      console.error('Error fetching collection day book:', error);
      alert('Failed to fetch collection day book');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    fetchCollectionDayBook();
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const summary = collectionData.summary || {};
    const collections = collectionData.collections || [];

    // Add title
    doc.setFontSize(18);
    doc.text('Collection Day Book Report', 14, 20);
    
    // Add date range
    doc.setFontSize(11);
    doc.text(`Date: ${summary.date_range || 'N/A'}`, 14, 30);
    
    // Add summary
    doc.text(`Total Collections: ₹${(summary.total_collections || 0).toLocaleString('en-IN')}`, 14, 38);
    doc.text(`Total Transactions: ${summary.total_transactions || 0}`, 14, 44);

    // Add table
    const tableData = collections.map(item => [
      item.date,
      item.receipt_no,
      item.student_name,
      item.admission_number,
      item.collection_type,
      item.payment_mode,
      `₹${item.amount.toLocaleString('en-IN')}`
    ]);

    doc.autoTable({
      startY: 52,
      head: [['Date', 'Receipt No.', 'Student Name', 'Admission No.', 'Type', 'Mode', 'Amount']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 8 }
    });

    // Add total at the bottom
    const finalY = doc.lastAutoTable.finalY || 52;
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Total: ₹${(summary.total_collections || 0).toLocaleString('en-IN')}`, 14, finalY + 10);

    doc.save(`Collection_DayBook_${fromDate}_to_${toDate}.pdf`);
  };

  const exportToExcel = () => {
    const summary = collectionData.summary || {};
    const collections = collectionData.collections || [];

    // Prepare data for Excel
    const excelData = collections.map(item => ({
      'Date': item.date,
      'Receipt No.': item.receipt_no,
      'Student Name': item.student_name,
      'Admission No.': item.admission_number,
      'Collection Type': item.collection_type,
      'Description': item.description,
      'Payment Mode': item.payment_mode,
      'Amount (₹)': item.amount
    }));

    // Add summary row
    excelData.push({});
    excelData.push({
      'Date': 'SUMMARY',
      'Receipt No.': '',
      'Student Name': '',
      'Admission No.': '',
      'Collection Type': `Total Transactions: ${summary.total_transactions || 0}`,
      'Description': '',
      'Payment Mode': 'Total:',
      'Amount (₹)': summary.total_collections || 0
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Collection Day Book');
    
    XLSX.writeFile(workbook, `Collection_DayBook_${fromDate}_to_${toDate}.xlsx`);
  };

  // Lightweight CSV export (no library dependency, Excel-compatible UTF-8 BOM)
  const exportToCSV = () => {
    const summary = collectionData.summary || {};
    const collections = collectionData.collections || [];
    if (collections.length === 0) return;

    const csvEscape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const headers = [
      'Date', 'Receipt No.', 'Student Name', 'Admission No.',
      'Collection Type', 'Description', 'Payment Mode', 'Amount (₹)'
    ];
    const lines = [headers.join(',')];
    collections.forEach((item) => {
      lines.push([
        item.date || '',
        item.receipt_no || '',
        item.student_name || '',
        item.admission_number || '',
        item.collection_type || '',
        item.description || '',
        item.payment_mode || '',
        item.amount ?? 0
      ].map(csvEscape).join(','));
    });
    // Summary footer row (blank line then totals)
    lines.push('');
    lines.push([
      'SUMMARY', '', '', '',
      `Total Transactions: ${summary.total_transactions || 0}`,
      '', 'Total:', summary.total_collections || 0
    ].map(csvEscape).join(','));

    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Collection_DayBook_${fromDate}_to_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const summary = collectionData.summary || {};
  const collections = collectionData.collections || [];

  // Get cash collection amount
  const cashCollection = summary.collection_by_mode?.['Cash'] || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Collection Day Book</h1>
        <p className="text-sm text-gray-500 mt-1">Daily fee collection register - All income sources</p>
      </div>

      {/* Date Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleApplyFilter}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
            >
              Apply Filter
            </Button>
            <Button 
              onClick={exportToCSV}
              data-testid="collection-daybook-export-csv-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 px-4"
              disabled={collections.length === 0}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
            <Button 
              onClick={exportToPDF}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 px-4"
              disabled={collections.length === 0}
            >
              <FileText className="h-4 w-4" />
              Export PDF
            </Button>
            <Button 
              onClick={exportToExcel}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 px-4"
              disabled={collections.length === 0}
            >
              <FileSpreadsheet className="h-4 w-4" />
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Collections</p>
              <p className="text-3xl font-bold text-green-600 mt-1">₹{(summary.total_collections || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.date_range || 'N/A'}</p>
            </div>
            <DollarSign className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Transactions</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{summary.total_transactions || 0}</p>
            </div>
            <TrendingUp className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Cash Collection</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">₹{cashCollection.toLocaleString('en-IN')}</p>
            </div>
            <DollarSign className="h-12 w-12 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Collection by Type Breakdown */}
      {summary.collection_by_type && Object.keys(summary.collection_by_type).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Collection by Type</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(summary.collection_by_type).map(([type, amount]) => (
              <div key={type} className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">{type}</p>
                <p className="text-xl font-bold text-gray-900 mt-1">₹{amount.toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collections Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading collection data...</p>
          </div>
        ) : collections.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No collections found for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt No.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admission No.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Collection Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Mode
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {collections.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.date || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.receipt_no && item.receipt_no !== 'null' ? item.receipt_no : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {item.student_name && item.student_name !== 'N/A' ? item.student_name : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.admission_number && item.admission_number !== 'N/A' ? item.admission_number : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {item.collection_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {item.description || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.payment_mode && item.payment_mode !== 'null' ? item.payment_mode : <span className="text-gray-400 italic">Cash</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-green-600">
                      ₹{item.amount.toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-gray-50 font-bold">
                  <td colSpan="7" className="px-6 py-4 text-right text-sm text-gray-900">
                    Total:
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-green-600 font-bold">
                    ₹{(summary.total_collections || 0).toLocaleString('en-IN')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionDayBook;